import { useMemo, useState } from "react";
import './DocumentCenter.css'; // Importiere die CSS-Datei für die Stile
import { toast } from "react-toastify";
import {
  CATEGORY_OPTIONS,
  DOCUMENT_TEMPLATES,
  DocumentPayload,
  DocumentStatus,
  ProcessingStep,
  ExtractedData,
  ExtractedField,
  TEMPLATE_CONFIGS,
  PATIENT_DATA,
  DEFAULT_CARE_SERVICES
} from "./DocumentCenterComponents";

import {
  DocumentCategory,
  TemplateFormData,
  TaskAssignment,
  SHARE_RECIPIENTS

} from "./DocumentCenterComponents";

import {
  DocumentTile,
  DocumentItem,
  StickyActionBar,
} from "./DocumentCenterComponents";

import PDFPreviewModal from "./PDFPreviewModal";
import pdfFile from './Pflegevertrag.pdf';


// ---- Hauptkomponente ----
export default function DocumentCenter() {
  const [view, setView] = useState<"categories" | "documents" | "upload" | "share" | "detail" | "template-form">("categories");
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<DocumentPayload | null>(null);
  const [documents, setDocuments] = useState<DocumentPayload[]>([...DOCUMENT_TEMPLATES]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [templateFormData, setTemplateFormData] = useState<TemplateFormData>({});
  const [taskAssignments, setTaskAssignments] = useState<TaskAssignment[]>([]);
  const [isSubmittingTemplate, setIsSubmittingTemplate] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewFields, setReviewFields] = useState<ExtractedField[]>([]);
  const [editedFieldValues, setEditedFieldValues] = useState<Record<string, string>>({});
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [currentPdfFile, setCurrentPdfFile] = useState<string>("");
  const [currentPdfName, setCurrentPdfName] = useState<string>("");


  // Gefilterte Dokumente basierend auf Kategorie und Suche
  const filteredDocuments = useMemo(() => {
    let docs = documents;

    if (selectedCategory && selectedCategory !== "eigene") {
      docs = docs.filter(doc => doc.category === selectedCategory);
    } else if (selectedCategory === "eigene") {
      // Zeige alle hochgeladenen und verarbeiteten Dokumente in "Eigene Dokumente"
      docs = docs.filter(doc =>
        doc.status === "hochgeladen" ||
        doc.status === "processing_upload" ||
        doc.status === "processing_ocr" ||
        doc.status === "review_required" ||
        doc.status === "accepted" ||
        doc.status === "failed"
      );
    }

    if (searchTerm) {
      docs = docs.filter(doc =>
        doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return docs;
  }, [documents, selectedCategory, searchTerm]);

  // Handler Funktionen
  function handleCategorySelect(category: DocumentCategory) {
    setSelectedCategory(category);
    setView("documents");
  }

  function handleDocumentSelect(document: DocumentPayload) {
    setSelectedDocument(document);
    setView("detail");
  }

  function handleDownload(doc: DocumentPayload) {
    // Prüfe, ob es sich um das spezifische PDF-Dokument handelt
    if (doc.title.includes("Pflegevertrag") || doc.fileUrl.includes("Pflegevertrag.pdf")) {
      // Öffne das PDF Preview Modal für die lokale PDF-Datei
      setCurrentPdfFile(pdfFile);
      setCurrentPdfName(doc.title);
      setPdfPreviewOpen(true);
    } else if (doc.fileUrl && doc.fileUrl.startsWith("/")) {
      // Für relative Pfade im Projekt - öffne PDF Preview Modal
      setCurrentPdfFile(doc.fileUrl);
      setCurrentPdfName(doc.title);
      setPdfPreviewOpen(true);
    } else {
      // Für andere Dokumenttypen - simuliere Download
      console.log("Download:", doc.title);
      toast(`Download von "${doc.title}" gestartet`);
    }
  }

  function handleShare(document: DocumentPayload) {
    setSelectedDocument(document);
    setView("share");
  }

  function handleDelete(documentId: string) {
    // Benutzerdefinierte Bestätigung statt window.confirm
    const shouldDelete = window.prompt("Möchten Sie dieses Dokument wirklich löschen? Geben 'JA' ein, um zu bestätigen:");
    if (shouldDelete === "JA") {
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    }
  }

  function handleUpload() {
    setView("upload");
    setUploadFile(null);
    setUploadTitle("");
    setUploadDescription("");
  }

  async function handleUploadSubmit() {
    if (!uploadFile || !uploadTitle.trim()) {
      toast("Bitte wählen Sie eine Datei aus und geben Sie einen Titel ein.");
      return;
    }

    setIsUploading(true);

    // Simuliere Upload
    await new Promise(resolve => setTimeout(resolve, 1500));

    const newDocumentId = `uploaded_${Date.now()}`;
    const newDocument: DocumentPayload = {
      id: newDocumentId,
      title: uploadTitle,
      description: uploadDescription,
      category: "eigene",
      fileUrl: URL.createObjectURL(uploadFile),
      fileSize: `${Math.round(uploadFile.size / 1024)} KB`,
      lastUpdated: new Date().toISOString().split('T')[0],
      status: "processing_upload", // Neuer Status
      processingProgress: 0, // Start des Fortschritts
      currentProcessingStep: "upload", // Erster Schritt
      uploadedBy: "Benutzer"
    };

    setDocuments(prev => [...prev, newDocument]);
    setIsUploading(false);
    setView("documents");
    setSelectedCategory("eigene");

    // Starte die Verarbeitungssimulation
    simulateDocumentProcessing(newDocumentId);
  }

  async function simulateDocumentProcessing(documentId: string) {
    const processingSteps: ProcessingStep[] = ["upload", "ocr", "classification", "field_detection", "validation"];

    for (let i = 0; i < processingSteps.length; i++) {
      const step = processingSteps[i];
      const progress = ((i + 1) / processingSteps.length) * 100;

      // Update Dokumentstatus und Fortschritt
      setDocuments(prev =>
        prev.map(doc =>
          doc.id === documentId
            ? {
              ...doc,
              status: i === 0 ? "processing_upload" : "processing_ocr", // Nach Upload ist OCR die Hauptphase
              currentProcessingStep: step,
              processingProgress: progress
            }
            : doc
        )
      );

      // Simuliere Dauer für jeden Schritt
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
    }

    // Nach Abschluss der Verarbeitung, bestimme das Endergebnis
    const randomOutcome = Math.random();
    let finalStatus: DocumentStatus;
    let extractedData: ExtractedData | undefined;

    if (randomOutcome < 0.1) { // 10% Fehler
      finalStatus = "failed";
    } else if (randomOutcome < 0.4) { // 30% Automatische Übernahme
      finalStatus = "accepted";
      extractedData = generateDummyExtractedData();
    } else { // 60% Review erforderlich
      finalStatus = "review_required";
      extractedData = generateDummyExtractedData();
    }

    setDocuments(prev =>
      prev.map(doc =>
        doc.id === documentId
          ? {
            ...doc,
            status: finalStatus,
            processingProgress: 100,
            currentProcessingStep: undefined,
            extractedData: extractedData,
            lastUpdated: new Date().toISOString().split('T')[0]
          }
          : doc
      )
    );
  }

  function generateDummyExtractedData(): ExtractedData {
    const documentTypes = ["Pflegegrad-Bescheid", "Kostenvoranschlag", "Arztbericht", "Medikationsplan"];
    const randomType = documentTypes[Math.floor(Math.random() * documentTypes.length)];

    const fields: ExtractedField[] = [
      {
        id: "pflegegrad",
        label: "Pflegegrad",
        value: `PG ${Math.floor(Math.random() * 5) + 1}`,
        confidence: 85 + Math.floor(Math.random() * 15),
        type: "select",
        options: ["1", "2", "3", "4", "5", "unbekannt"]
      },
      {
        id: "pflegekasse",
        label: "Pflegekasse",
        value: ["AOK", "TK", "DAK", "Knappschaft"][Math.floor(Math.random() * 4)],
        confidence: 90 + Math.floor(Math.random() * 10),
        type: "text"
      },
      {
        id: "gueltig_bis",
        label: "Gültig bis",
        value: new Date(Date.now() + Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        confidence: 95 + Math.floor(Math.random() * 5),
        type: "date"
      }
    ];

    const deviations: string[] = [];
    if (Math.random() > 0.7) {
      deviations.push("abweichend zum Profil: Kasse war 'TK'");
    }

    return {
      documentType: randomType,
      fields: fields,
      deviations: deviations
    };
  }

  async function handleShareSubmit() {
    if (!selectedDocument || selectedServices.length === 0) {
      toast("Bitte wählen Sie mindestens einen Pflegedienst aus.");
      return;
    }

    setIsSharing(true);

    // Simuliere Sharing
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Update Dokumentstatus
    setDocuments(prev =>
      prev.map(doc =>
        doc.id === selectedDocument.id
          ? { ...doc, status: "geteilt" as DocumentStatus, sharedWith: selectedServices }
          : doc
      )
    );

    setIsSharing(false);
    setView("documents");

    const serviceNames = selectedServices
      .map(id => DEFAULT_CARE_SERVICES.find(s => s.id === id)?.name)
      .filter(Boolean)
      .join(", ");

    toast(`Dokument "${selectedDocument.title}" wurde erfolgreich geteilt.`);
  }

  function handleBack() {
    if (view === "documents" || view === "upload") {
      setView("categories");
      setSelectedCategory(null);
    } else if (view === "share" || view === "detail") {
      setView("documents");
      setSelectedDocument(null);
    } else if (view === "template-form") {
      setView("documents");
      setSelectedDocument(null);
      setTemplateFormData({});
      setTaskAssignments([]);
    }
  }

  // Vorlagen-Handler Funktionen
  function handleOpenTemplate(document: DocumentPayload) {
    setSelectedDocument(document);
    initializeTemplateForm(document.id);
    setView("template-form");
  }

  function initializeTemplateForm(templateId: string) {
    const config = TEMPLATE_CONFIGS[templateId];
    if (!config) return;

    const initialData: TemplateFormData = {};

    // Formular mit Patientendaten vorausfüllen
    config.forEach(field => {
      switch (field.id) {
        case "klient_name":
          initialData[field.id] = `${PATIENT_DATA.firstName} ${PATIENT_DATA.lastName}`;
          break;
        case "klient_adresse":
          initialData[field.id] = PATIENT_DATA.addresse;
          break;
        case "klient_telefon":
          initialData[field.id] = PATIENT_DATA.phoneNumber;
          break;
        case "klient_verfuegbarkeit":
          initialData[field.id] = PATIENT_DATA.clientAvailability;
          break;
        case "pflegegrad":
          const pflegegrad = PATIENT_DATA.productCriteriaList.find(c => c.criteriaName === "Pflegegrad/-stufe")?.value;
          initialData[field.id] = pflegegrad || "3";
          break;
        case "krankenkasse":
          initialData[field.id] = PATIENT_DATA.krankenkasse || "";
          break;
        case "versicherungsnummer":
          initialData[field.id] = PATIENT_DATA.versicherungsnummer || "";
          break;
        default:
          initialData[field.id] = field.defaultValue || "";
      }
    });

    setTemplateFormData(initialData);
    setTaskAssignments([]);
  }

  function handleTemplateFieldChange(fieldId: string, value: string | boolean | string[]) {
    setTemplateFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  }

  function handleAddTask() {
    const newTask: TaskAssignment = {
      id: `task_${Date.now()}`,
      title: "",
      description: "",
      assignedTo: "klient",
      priority: "medium",
      completed: false
    };
    setTaskAssignments(prev => [...prev, newTask]);
  }

  function handleTaskChange(taskId: string, field: keyof TaskAssignment, value: any) {
    setTaskAssignments(prev =>
      prev.map(task =>
        task.id === taskId ? { ...task, [field]: value } : task
      )
    );
  }

  function handleDeleteTask(taskId: string) {
    setTaskAssignments(prev => prev.filter(task => task.id !== taskId));
  }

  async function handleTemplateSubmit() {
    if (!selectedDocument) return;

    setIsSubmittingTemplate(true);

    // Simuliere Speichern
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Hier würde das Dokument mit den Formulardaten gespeichert werden
    console.log("Template Daten:", templateFormData);
    console.log("Aufgaben:", taskAssignments);

    // Update Dokumentstatus von "vorlage" zu "erstellt"
    setDocuments(prev =>
      prev.map(doc =>
        doc.id === selectedDocument.id
          ? { ...doc, status: "erstellt" as DocumentStatus, lastUpdated: new Date().toISOString().split('T')[0] }
          : doc
      )
    );

    setIsSubmittingTemplate(false);
    setView("documents");
    setSelectedDocument(null);

    toast(`Vorlage "${selectedDocument.title}" wurde erfolgreich ausgefüllt und gespeichert!`);

    // Aufgaben wurden erstellt und können im Task-Management gesehen werden
    if (taskAssignments.length > 0) {
      toast(`${taskAssignments.length} Aufgaben wurden erstellt und zugewiesen.`);
    }
  }

  // Handler für die neuen Dokumenten-Aktionen
  function handleReviewResult(document: DocumentPayload) {
    setSelectedDocument(document);
    if (document.extractedData && document.extractedData.fields.length > 0) {
      setReviewFields(document.extractedData.fields);
      const initialValues: Record<string, string> = {};
      document.extractedData.fields.forEach(field => {
        initialValues[field.id] = field.value;
      });
      setEditedFieldValues(initialValues);
      setShowReviewModal(true);
    } else {
      setView("detail");
      toast(`Ergebnis für "${document.title}" anzeigen. In einer vollständigen Implementierung würde hier eine detaillierte Review-Ansicht geöffnet.`);
    }
  }

  // Modal Handler Funktionen
  function handleFieldChange(fieldId: string, value: string) {
    setEditedFieldValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  }

  function handleAcceptReview() {
    if (!selectedDocument) return;

    // Update the extracted data with edited values
    const updatedFields = reviewFields.map(field => ({
      ...field,
      value: editedFieldValues[field.id] || field.value
    }));

    // Update document status to accepted
    setDocuments(prev =>
      prev.map(doc =>
        doc.id === selectedDocument.id
          ? {
            ...doc,
            status: "accepted" as DocumentStatus,
            extractedData: {
              ...doc.extractedData!,
              fields: updatedFields
            }
          }
          : doc
      )
    );

    setShowReviewModal(false);

    // Show success message with extracted data
    const extractedFields = updatedFields.filter(field => field.confidence >= 80);
    let updatedFieldsList: string[] = [];

    extractedFields.forEach(field => {
      switch (field.id) {
        case "pflegegrad":
          const pgValue = field.value.replace("PG ", "").trim();
          if (["1", "2", "3", "4", "5"].includes(pgValue)) {
            updatedFieldsList.push(`Pflegegrad: ${pgValue}`);
          }
          break;
        case "pflegekasse":
          if (field.value && field.value.trim() !== "") {
            updatedFieldsList.push(`Pflegekasse: ${field.value}`);
          }
          break;
        case "gueltig_bis":
          if (field.value && field.value.trim() !== "") {
            updatedFieldsList.push(`Gültig bis: ${field.value}`);
          }
          break;
      }
    });

    if (updatedFieldsList.length > 0) {
      toast(`Dokument "${selectedDocument.title}" wurde erfolgreich übernommen!\n\nFolgende Daten wurden extrahiert und können zur Aktualisierung des Patientenprofils verwendet:\n${updatedFieldsList.join("\n")}`);
    } else {
      toast(`Dokument "${selectedDocument.title}" wurde erfolgreich übernommen!`);
    }
  }

  function handleRejectReview() {
    if (!selectedDocument) return;

    // Update document status to failed
    setDocuments(prev =>
      prev.map(doc =>
        doc.id === selectedDocument.id
          ? { ...doc, status: "failed" as DocumentStatus }
          : doc
      )
    );

    setShowReviewModal(false);
    toast(`Dokument "${selectedDocument.title}" wurde abgelehnt. Bitte laden Sie ein besseres Dokument hoch.`);
  }

  function handleAcceptResult(document: DocumentPayload) {
    // Für Dokumente mit Status "review_required" auf "accepted" setzen
    if (document.status === "review_required") {
      setDocuments(prev =>
        prev.map(doc =>
          doc.id === document.id
            ? { ...doc, status: "accepted" as DocumentStatus }
            : doc
        )
      );

      // Extrahierte Daten verarbeiten und auf Patientendaten anwenden
      if (document.extractedData && document.extractedData.fields.length > 0) {
        const extractedFields = document.extractedData.fields;
        let updatedFields: string[] = [];

        // Prüfe, ob relevante Felder extrahiert wurden
        extractedFields.forEach(field => {
          if (field.confidence >= 80) { // Nur Felder mit hoher Konfidenz berücksichtigen
            switch (field.id) {
              case "pflegegrad":
                const pgValue = field.value.replace("PG ", "").trim();
                if (["1", "2", "3", "4", "5"].includes(pgValue)) {
                  updatedFields.push(`Pflegegrad: ${pgValue}`);
                }
                break;
              case "pflegekasse":
                if (field.value && field.value.trim() !== "") {
                  updatedFields.push(`Pflegekasse: ${field.value}`);
                }
                break;
              case "gueltig_bis":
                if (field.value && field.value.trim() !== "") {
                  updatedFields.push(`Gültig bis: ${field.value}`);
                }
                break;
            }
          }
        });

        if (updatedFields.length > 0) {
          toast(`Dokument "${document.title}" wurde erfolgreich übernommen!\n\nFolgende Daten wurden extrahiert und können zur Aktualisierung des Patientenprofils verwendet:\n${updatedFields.join("\n")}`);
        } else {
          toast(`Dokument "${document.title}" wurde erfolgreich übernommen!`);
        }
      } else {
        toast(`Dokument "${document.title}" wurde erfolgreich übernommen!`);
      }
    } else {
      // Für bereits akzeptierte Dokumente einfach die Details anzeigen
      setSelectedDocument(document);
      setView("detail");
    }
  }

  function handleRetryProcessing(document: DocumentPayload) {
    // Setze das Dokument zurück auf den Verarbeitungsstatus
    setDocuments(prev =>
      prev.map(doc =>
        doc.id === document.id
          ? {
            ...doc,
            status: "processing_upload" as DocumentStatus,
            processingProgress: 0,
            currentProcessingStep: "upload"
          }
          : doc
      )
    );

    // Starte die Verarbeitungssimulation erneut
    simulateDocumentProcessing(document.id);

    toast(`Verarbeitung von "${document.title}" wird erneut gestartet.`);
  }

  // Funktion zur Anzeige von extrahierten Daten
  function renderExtractedData(extractedData: ExtractedData) {
    return (
      <div className="extracted-data-section">
        <h3>Extrahierte Daten</h3>

        {extractedData.documentType && (
          <div className="data-item">
            <span className="data-label">Dokumenttyp:</span>
            <span className="data-value">{extractedData.documentType}</span>
          </div>
        )}

        <div className="extracted-fields">
          <h4>Erkannte Felder:</h4>
          <div className="fields-grid">
            {extractedData.fields.map((field, index) => (
              <div key={index} className="field-item">
                <div className="field-header">
                  <span className="field-name">{field.label}</span>
                  <span className={`confidence-badge ${field.confidence >= 90 ? 'high' : field.confidence >= 70 ? 'medium' : 'low'}`}>
                    {field.confidence}%
                  </span>
                </div>
                <div className="field-value">{field.value}</div>

                {field.sourcePage && (
                  <div className="field-source">Seite: {field.sourcePage}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {extractedData.deviations && extractedData.deviations.length > 0 && (
          <div className="deviations-section">
            <h4>Abweichungen:</h4>
            <div className="deviations-list">
              {extractedData.deviations.map((deviation, index) => (
                <div key={index} className="deviation-item warning">
                  ⚠️ {deviation}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render Funktionen für verschiedene Ansichten
  function renderCategories() {
    return (
      <div className="categories-view">
        <div className="view-header">
          <h2 className="view-title">Dokumenten-Center</h2>
          <p className="view-subtitle">Wählen Sie eine Kategorie aus, um auf die Dokumente zuzugreifen</p>
        </div>

        <div className="categories-grid">
          {CATEGORY_OPTIONS.map((category) => (
            <DocumentTile
              key={category.value}
              title={category.label}
              emoji={category.emoji}
              count={category.count}
              onClick={() => handleCategorySelect(category.value)}
            />
          ))}
        </div>
      </div>
    );
  }

  function renderDocuments() {
    const categoryLabel = CATEGORY_OPTIONS.find(c => c.value === selectedCategory)?.label || "Dokumente";

    return (
      <div className="documents-view">
        <div className="view-header">
          <h2 className="view-title">{categoryLabel}</h2>
          <div className="search-upload-row">
            <div className="search-box">
              <input
                type="text"
                placeholder="Dokumente durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-2"
              />
            </div>
            <button className="btn btn-primary btn-medium-small" onClick={handleUpload}>
              Hochladen
            </button>
          </div>
        </div>

        <div className="documents-list">
          {filteredDocuments.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📄</div>
              <h3>Keine Dokumente gefunden</h3>
              <p>
                {searchTerm
                  ? `Keine Dokumente gefunden für "${searchTerm}"`
                  : selectedCategory === "eigene"
                    ? "Sie haben noch keine eigenen Dokumente für diesen Klienten hochgeladen."
                    : "In dieser Kategorie sind keine Dokumente verfügbar."
                }
              </p>
              {selectedCategory === "eigene" && (
                <button className="btn btn-primary btn-medium-small" onClick={handleUpload}>
                  Dokument hochladen
                </button>
              )}
            </div>
          ) : (
            filteredDocuments.map((document) => (
              <DocumentItem
                key={document.id}
                document={document}
                onView={() => handleDocumentSelect(document)}
                onDownload={() => handleDownload(document)}
                onShare={() => handleShare(document)}
                onDelete={() => handleDelete(document.id)}
                onFillTemplate={document.status === "vorlage" ? () => handleOpenTemplate(document) : undefined}
                onReviewResult={() => handleReviewResult(document)}
                onAcceptResult={() => handleAcceptResult(document)}
                onRetryProcessing={() => handleRetryProcessing(document)}
              />
            ))
          )}
        </div>
      </div>
    );
  }

  function renderUpload() {
    return (
      <div className="upload-view">
        <div className="view-header">
          <h2 className="view-title">Dokument hochladen</h2>
          <p className="view-subtitle">Laden Sie ein eigenes Dokument für die Pflegeverwaltung hoch</p>
        </div>

        <div className="upload-form">
          <div className="upload-zone">
            <input
              type="file"
              id="file-upload"
              accept=".pdf,.doc,.docx,.jpg,.png"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              style={{ display: 'none' }}
            />
            <label htmlFor="file-upload" className="upload-label">
              <div className="upload-icon">📁</div>
              <h3>Datei auswählen</h3>
              <p>PDF, DOC, DOCX, JPG, PNG (max. 10MB)</p>
              {uploadFile && (
                <div className="selected-file">
                  <span className="file-name">📎 {uploadFile.name}</span>
                  <span className="file-size">{Math.round(uploadFile.size / 1024)} KB</span>
                </div>
              )}
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Titel *</label>
            <input
              type="text"
              className="form-input"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              placeholder="z.B. Pflegevertrag Müller"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Beschreibung</label>
            <textarea
              className="form-textarea"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              placeholder="Kurze Beschreibung des Dokuments..."
              rows={3}
            />
          </div>
        </div>
      </div>
    );
  }

  function renderShare() {
    if (!selectedDocument) return null;

    // Helper-Funktion für Icons basierend auf Typ
    const getRecipientIcon = (type: string, insuranceType?: string) => {
      switch (type) {
        case "pflegedienst": return "🏥";
        case "patient": return "👤";
        case "angehöriger": return "👨‍👩‍👧‍👦";
        case "versicherung":
          switch (insuranceType) {
            case "GKV": return "🏛️";
            case "PKV": return "💼";
            case "DRV": return "🏦";
            default: return "🛡️";
          }
        default: return "📧";
      }
    };

    // Helper-Funktion für Typ-Badge
    const getTypeBadge = (type: string, insuranceType?: string) => {
      switch (type) {
        case "pflegedienst": return <span className="type-badge pflegedienst">Pflegedienst</span>;
        case "patient": return <span className="type-badge patient">Patient</span>;
        case "angehöriger": return <span className="type-badge angehöriger">Angehöriger</span>;
        case "versicherung":
          switch (insuranceType) {
            case "GKV": return <span className="type-badge versicherung gkv">GKV</span>;
            case "PKV": return <span className="type-badge versicherung pkv">PKV</span>;
            case "DRV": return <span className="type-badge versicherung drv">DRV</span>;
            default: return <span className="type-badge versicherung">Versicherung</span>;
          }
        default: return null;
      }
    };

    // Gruppiere Empfänger nach Typ
    const pflegedienste = SHARE_RECIPIENTS.filter(r => r.type === "pflegedienst");
    const personen = SHARE_RECIPIENTS.filter(r => r.type === "patient" || r.type === "angehöriger");
    const versicherungen = SHARE_RECIPIENTS.filter(r => r.type === "versicherung");

    return (
      <div className="share-view">
        <div className="view-header">
          <h2 className="view-title">Dokument teilen</h2>
          <p className="view-subtitle">
            Teilen Sie "{selectedDocument.title}" mit verschiedenen Empfängern
          </p>
        </div>

        <div className="share-form">
          <div className="document-preview">
            <div className="preview-icon">📄</div>
            <div className="preview-info">
              <h4>{selectedDocument.title}</h4>
              <p>{selectedDocument.description}</p>
              <span className="file-size">{selectedDocument.fileSize}</span>
            </div>
          </div>


          {/* Patienten und Angehörige */}
          <div className="share-section">
            <div className="share-section-header">
              <h3>👥 Patienten & Angehörige</h3>
              <span className="section-count">{personen.length} verfügbar</span>
            </div>
            <div className="services-grid">
              {personen.map((recipient) => (
                <label key={recipient.id} className="service-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedServices.includes(recipient.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedServices(prev => [...prev, recipient.id]);
                      } else {
                        setSelectedServices(prev => prev.filter(id => id !== recipient.id));
                      }
                    }}
                  />
                  <div className="service-info">
                    <div className="service-header">
                      <span className="service-icon">{getRecipientIcon(recipient.type)}</span>
                      <div className="service-name">{recipient.name}</div>
                      {getTypeBadge(recipient.type)}
                    </div>
                    <div className="service-contact">{recipient.email}</div>
                    {recipient.phone && <div className="service-phone">{recipient.phone}</div>}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Versicherungen */}
          <div className="share-section">
            <div className="share-section-header">
              <h3>🛡️ Versicherungen</h3>
              <span className="section-count">{versicherungen.length} verfügbar</span>
            </div>
            <div className="services-grid">
              {versicherungen.map((recipient) => (
                <label key={recipient.id} className="service-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedServices.includes(recipient.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedServices(prev => [...prev, recipient.id]);
                      } else {
                        setSelectedServices(prev => prev.filter(id => id !== recipient.id));
                      }
                    }}
                  />
                  <div className="service-info">
                    <div className="service-header">
                      <span className="service-icon">{getRecipientIcon(recipient.type, recipient.insuranceType)}</span>
                      <div className="service-name">{recipient.name}</div>
                      {getTypeBadge(recipient.type, recipient.insuranceType)}
                    </div>
                    <div className="service-contact">{recipient.email}</div>
                    {recipient.phone && <div className="service-phone">{recipient.phone}</div>}
                  </div>
                </label>
              ))}
            </div>
          </div>




          {selectedServices.length > 0 && (
            <div className="selected-services-summary">
              <h4>Ausgewählte Empfänger ({selectedServices.length})</h4>
              <div className="selected-list">
                {selectedServices.map(id => {
                  const recipient = SHARE_RECIPIENTS.find(r => r.id === id);
                  return recipient ? (
                    <span key={id} className="selected-service-tag">
                      <span className="tag-icon">{getRecipientIcon(recipient.type, recipient.insuranceType)}</span>
                      {recipient.name}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderDetail() {
    if (!selectedDocument) return null;

    return (
      <div className="detail-view">
        <div className="view-header">
          <h2 className="view-title">{selectedDocument.title}</h2>
          <p className="view-subtitle">{selectedDocument.description}</p>
        </div>

        <div className="document-detail">
          <div className="detail-preview">
            <div className="preview-placeholder">
              <div className="preview-icon-large">📄</div>
              <h3>Dokumentenvorschau</h3>
              <p>Dies ist eine Platzhaltervorschau für das Dokument</p>
              <div className="preview-actions">
                <button className="btn btn-primary btn-medium-small">
                  📖 Vollbildansicht
                </button>
              </div>
            </div>
          </div>

          <div className="detail-info">
            <div className="info-section">
              <h3>Informationen</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Dateigröße:</span>
                  <span className="info-value">{selectedDocument.fileSize}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Zuletzt aktualisiert:</span>
                  <span className="info-value">{selectedDocument.lastUpdated}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status:</span>
                  <span className="info-value">
                    {selectedDocument.status === "vorlage" && "Vorlage"}
                    {selectedDocument.status === "hochgeladen" && "Hochgeladen"}
                    {selectedDocument.status === "geteilt" && "Geteilt"}
                    {selectedDocument.status === "processing_upload" && "Wird hochgeladen"}
                    {selectedDocument.status === "processing_ocr" && "Wird verarbeitet"}
                    {selectedDocument.status === "review_required" && "Prüfung erforderlich"}
                    {selectedDocument.status === "accepted" && "Übernommen"}
                    {selectedDocument.status === "failed" && "Fehlgeschlagen"}
                    {selectedDocument.status === "erstellt" && "Erstellt"}
                  </span>
                </div>
                {selectedDocument.uploadedBy && (
                  <div className="info-item">
                    <span className="info-label">Hochgeladen von:</span>
                    <span className="info-value">{selectedDocument.uploadedBy}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Extrahierte Daten anzeigen, wenn verfügbar */}
            {selectedDocument.extractedData && (
              <div className="info-section">
                {renderExtractedData(selectedDocument.extractedData)}
              </div>
            )}

            {selectedDocument.sharedWith && selectedDocument.sharedWith.length > 0 && (
              <div className="info-section">
                <h3>Geteilt mit</h3>
                <div className="shared-services">
                  {selectedDocument.sharedWith.map(serviceId => {
                    const service = DEFAULT_CARE_SERVICES.find(s => s.id === serviceId);
                    return service ? (
                      <div key={serviceId} className="shared-service">
                        <div className="service-name">{service.name}</div>
                        <div className="service-email">{service.email}</div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render Funktion für Vorlagen-Formular
  function renderTemplateForm() {
    if (!selectedDocument) return null;

    const config = TEMPLATE_CONFIGS[selectedDocument.id];
    if (!config) {
      return (
        <div className="template-form-view">
          <div className="view-header">
            <h2 className="view-title">Vorlage nicht verfügbar</h2>
            <p className="view-subtitle">Diese Vorlage hat keine Formularkonfiguration.</p>
          </div>
        </div>
      );
    }

    // Helper-Funktion, um zu prüfen, ob ein Feld vorausgefüllt wurde
    const isPrefilled = (fieldId: string): boolean => {
      const value = templateFormData[fieldId];
      if (value === null || value === undefined || value === "") return false;
      if (typeof value === "boolean") return false;
      if (Array.isArray(value)) return value.length > 0;
      return String(value).trim() !== "";
    };

    return (
      <div className="template-form-view">
        <div className="view-header">
          <h2 className="view-title">{selectedDocument.title} ausfüllen</h2>
          <p className="view-subtitle">
            Füllen Sie das Formular aus. Die Felder wurden mit Patientendaten vorausgefüllt.
          </p>
        </div>

        <div className="template-form">
          {/* Formularfelder - fancy Grid Layout */}
          <div className="form-section">
            <div className="fancy-form-header">
              <h3>Formulardaten</h3>
              <div className="field-legend">
                <span className="legend-item prefilled">
                  <span className="legend-dot"></span> Ausgefüllt
                </span>
                <span className="legend-item empty">
                  <span className="legend-dot"></span> Auszufüllen
                </span>
              </div>
            </div>

            <div className="fancy-form-grid">
              {config.map((field) => {
                const prefilled = isPrefilled(field.id);
                return (
                  <div
                    key={field.id}
                    className={`fancy-form-group ${prefilled ? 'prefilled' : 'empty'} ${field.required ? 'required' : ''}`}
                  >
                    <div className="fancy-form-label">
                      <label>
                        {field.label}
                        {field.required && <span className="required-mark">*</span>}
                      </label>
                      <span className={`field-status ${prefilled ? 'prefilled' : 'empty'}`}>
                        {prefilled ? '✓ Ausgefüllt' : '+ Auszufüllen'}
                      </span>
                    </div>

                    <div className="fancy-form-input">
                      {field.type === "text" && (
                        <input
                          type="text"
                          className={`form-input ${prefilled ? 'prefilled-input' : 'empty-input'}`}
                          value={(templateFormData[field.id] as string) || ""}
                          onChange={(e) => handleTemplateFieldChange(field.id, e.target.value)}
                          placeholder={field.placeholder}
                          required={field.required}
                        />
                      )}
                      {field.type === "textarea" && (
                        <textarea
                          className={`form-textarea ${prefilled ? 'prefilled-input' : 'empty-input'}`}
                          value={(templateFormData[field.id] as string) || ""}
                          onChange={(e) => handleTemplateFieldChange(field.id, e.target.value)}
                          placeholder={field.placeholder}
                          rows={3}
                          required={field.required}
                        />
                      )}
                      {field.type === "select" && field.options && (
                        <select
                          className={`form-select ${prefilled ? 'prefilled-input' : 'empty-input'}`}
                          value={(templateFormData[field.id] as string) || ""}
                          onChange={(e) => handleTemplateFieldChange(field.id, e.target.value)}
                          required={field.required}
                        >
                          <option value="">Bitte wählen...</option>
                          {field.options.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      )}
                      {field.type === "date" && (
                        <input
                          type="date"
                          className={`form-input ${prefilled ? 'prefilled-input' : 'empty-input'}`}
                          value={(templateFormData[field.id] as string) || ""}
                          onChange={(e) => handleTemplateFieldChange(field.id, e.target.value)}
                          required={field.required}
                        />
                      )}
                      {field.type === "checkbox" && (
                        <label className="fancy-checkbox-label">
                          <input
                            type="checkbox"
                            checked={(templateFormData[field.id] as boolean) || false}
                            onChange={(e) => handleTemplateFieldChange(field.id, e.target.checked)}
                          />
                          <span>{field.placeholder}</span>
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Aufgaben-Erstellung - fancy Design */}
          <div className="form-section">
            <div className="fancy-section-header">
              <h3>Aufgaben erstellen</h3>
              <button
                type="button"
                className="btn btn-primary btn-medium"
                onClick={handleAddTask}
              >
                Aufgabe erstellen
              </button>
            </div>

            {taskAssignments.length === 0 ? (
              <div className="fancy-empty-tasks">
                <div className="empty-tasks-icon">📋</div>
                <p>Noch keine Aufgaben erstellt</p>
                <p className="empty-tasks-subtitle">Klicken Sie auf "Aufgabe erstellen", um Aufgaben hinzuzufügen</p>
              </div>
            ) : (
              <div className="fancy-tasks-container">
                {taskAssignments.map((task) => (
                  <div key={task.id} className="fancy-task-card">
                    <div className="fancy-task-header">
                      <div className="fancy-task-title-section">
                        <input
                          type="text"
                          className="fancy-task-title-input"
                          placeholder="Aufgabentitel eingeben..."
                          value={task.title}
                          onChange={(e) => handleTaskChange(task.id, "title", e.target.value)}
                        />
                        <div className="fancy-task-meta-icons">
                          <span className={`priority-icon priority-${task.priority}`}>
                            {task.priority === "high" ? "🔥" : task.priority === "medium" ? "⚡" : "💧"}
                          </span>
                          <span className="assign-icon">
                            {task.assignedTo === "klient" ? "👤" : task.assignedTo === "angehöriger" ? "👨‍👩‍👧‍👦" : "👨‍💼"}
                          </span>
                        </div>
                      </div>
                      <div className="fancy-task-actions">
                        <button
                          type="button"
                          className="btn btn-success btn-small fancy-save-task-btn"
                          onClick={() => {
                            if (task.title.trim()) {
                              toast(`Aufgabe "${task.title}" wurde erfolgreich erstellt und zugewiesen!`);
                            } else {
                              toast("Bitte geben Sie einen Aufgabentitel ein.");
                            }
                          }}
                          disabled={!task.title.trim()}
                        >
                          ✓ Speichern
                        </button>
                        <button
                          type="button"
                          className="btn-icon fancy-delete-btn"
                          onClick={() => handleDeleteTask(task.id)}
                          title="Aufgabe löschen"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>

                    <div className="fancy-task-content">
                      <textarea
                        className="fancy-task-description"
                        placeholder="Beschreiben Sie die Aufgabe genauer..."
                        value={task.description}
                        onChange={(e) => handleTaskChange(task.id, "description", e.target.value)}
                        rows={3}
                      />

                      <div className="fancy-task-controls">
                        <div className="control-group">
                          <label className="control-label">Zuweisen an:</label>
                          <select
                            className="fancy-select"
                            value={task.assignedTo}
                            onChange={(e) => handleTaskChange(task.id, "assignedTo", e.target.value)}
                          >
                            <option value="klient">👤 Klient</option>
                            <option value="angehöriger">👨‍👩‍👧‍👦 Angehöriger</option>
                            <option value="mitarbeiter">👨‍💼 Mitarbeiter</option>
                          </select>
                        </div>

                        <div className="control-group">
                          <label className="control-label">Priorität:</label>
                          <select
                            className="fancy-select"
                            value={task.priority}
                            onChange={(e) => handleTaskChange(task.id, "priority", e.target.value)}
                          >
                            <option value="low">💧 Niedrig</option>
                            <option value="medium">⚡ Mittel</option>
                            <option value="high">🔥 Hoch</option>
                          </select>
                        </div>

                        <div className="control-group">
                          <label className="control-label">Fällig am:</label>
                          <input
                            type="date"
                            className="fancy-date-input"
                            value={task.dueDate || ""}
                            onChange={(e) => handleTaskChange(task.id, "dueDate", e.target.value)}
                            placeholder="Fälligkeitsdatum"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Fancy Review Modal Component
  function renderReviewModal() {
    if (!showReviewModal || !selectedDocument) return null;

    return (
      <div className="modal-overlay">
        <div className="fancy-review-modal">
          <div className="modal-header-2">
            <div className="modal-title-section">
              <h2 className="modal-title">📊 Dokument prüfen</h2>
              <p className="modal-subtitle">
                Überprüfen und bestätigen Sie die extrahierten Daten aus "{selectedDocument.title}"
              </p>
            </div>
            <button
              className="modal-close-btn"
              onClick={() => setShowReviewModal(false)}
            >
              ✕
            </button>
          </div>

          <div className="modal-content-2">
            {/* Dokumentinfo */}
            <div className="document-info-card">
              <div className="document-info-header">
                <span className="document-icon">📄</span>
                <div>
                  <h3>{selectedDocument.title}</h3>
                  <p>{selectedDocument.description}</p>
                </div>
              </div>
              <div className="document-meta">
                <span className="meta-item">📅 {selectedDocument.lastUpdated}</span>
                <span className="meta-item">📦 {selectedDocument.fileSize}</span>
                <span className="meta-item status-badge review-required">🟠 Prüfung erforderlich</span>
              </div>
            </div>

            {/* Extrahierte Felder */}
            <div className="extracted-fields-section">
              <div className="section-header">
                <h3>🔍 Erkannte Felder ({reviewFields.length})</h3>
                <div className="confidence-legend">
                  <span className="legend-item high">
                    <span className="legend-dot"></span> Hoch (90-100%)
                  </span>
                  <span className="legend-item medium">
                    <span className="legend-dot"></span> Mittel (70-89%)
                  </span>
                  <span className="legend-item low">
                    <span className="legend-dot"></span> Niedrig (unter 70%)
                  </span>
                </div>
              </div>

              <div className="fields-grid-fancy">
                {reviewFields.map((field) => (
                  <div key={field.id} className="field-card-fancy">
                    <div className="field-card-header">
                      <div className="field-title-section">
                        <span className="field-icon">📝</span>
                        <span className="field-label">{field.label}</span>
                      </div>
                      <span className={`confidence-badge-fancy ${field.confidence >= 90 ? 'high' : field.confidence >= 70 ? 'medium' : 'low'}`}>
                        {field.confidence}%
                      </span>
                    </div>

                    <div className="field-input-section">
                      {field.type === "select" && field.options ? (
                        <select
                          className="fancy-field-select"
                          value={editedFieldValues[field.id] || field.value}
                          onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        >
                          {field.options.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : field.type === "date" ? (
                        <input
                          type="date"
                          className="fancy-field-input"
                          value={editedFieldValues[field.id] || field.value}
                          onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        />
                      ) : (
                        <input
                          type="text"
                          className="fancy-field-input"
                          value={editedFieldValues[field.id] || field.value}
                          onChange={(e) => handleFieldChange(field.id, e.target.value)}
                          placeholder={field.label}
                        />
                      )}
                    </div>

                    {/* Confidence Indicator Bar */}
                    <div className="confidence-bar-container">
                      <div className="confidence-label">Konfidenz</div>
                      <div className="confidence-bar-bg">
                        <div
                          className="confidence-bar-fill"
                          style={{
                            width: `${field.confidence}%`,
                            backgroundColor: field.confidence >= 90 ? '#4CAF50' : field.confidence >= 70 ? '#FF9800' : '#F44336'
                          }}
                        ></div>
                      </div>
                      <span className="confidence-percentage">{field.confidence}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Zusammenfassung und Hinweise */}
            <div className="review-summary">
              <div className="summary-card">
                <h4>📋 Zusammenfassung</h4>
                <ul className="summary-list">
                  <li>✅ {reviewFields.length} Felder wurden automatisch erkannt</li>
                  <li>📊 Durchschnittliche Konfidenz: {
                    Math.round(reviewFields.reduce((sum, field) => sum + field.confidence, 0) / reviewFields.length)
                  }%</li>
                  <li>🔍 {reviewFields.filter(f => f.confidence >= 90).length} Felder mit hoher Konfidenz</li>
                  <li>⚠️ {reviewFields.filter(f => f.confidence < 70).length} Felder mit niedriger Konfidenz</li>
                </ul>
              </div>

              <div className="hint-card info">
                <h4>💡 Hinweis</h4>
                <p>Überprüfen Sie die erkannten Felder und korrigieren Sie bei Bedarf. Felder mit hoher Konfidenz (grün) wurden wahrscheinlich korrekt erkannt.</p>
              </div>
            </div>
          </div>

          {/* Modal Actions */}
          <div className="modal-actions-2">
            <button
              className="btn btn-danger btn-large"
              onClick={handleRejectReview}
            >
              🗑️ Ablehnen
            </button>
            <button
              className="btn btn-success btn-large"
              onClick={handleAcceptReview}
            >
              ✓ Übernehmen & Speichern
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Haupt-Render ----
  return (
    <section
      className="document-scenter"
    >
      {/* Hauptinhalt basierend auf aktueller Ansicht */}
      <div className="view-container">
        {view === "categories" && renderCategories()}
        {view === "documents" && renderDocuments()}
        {view === "upload" && renderUpload()}
        {view === "share" && renderShare()}
        {view === "detail" && renderDetail()}
        {view === "template-form" && renderTemplateForm()}
      </div>

      {/* Fancy Review Modal */}
      {renderReviewModal()}

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        file={currentPdfFile}
        fileName={currentPdfName}
        isOpen={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
      />

      {/* Sticky Action Bar */}
      <StickyActionBar
        title={
          view === "categories" ? "Dokumenten-Center" :
            view === "documents" ? `${CATEGORY_OPTIONS.find(c => c.value === selectedCategory)?.label || "Dokumente"} (${filteredDocuments.length})` :
              view === "upload" ? "Dokument hochladen" :
                view === "share" ? "Dokument teilen" :
                  view === "detail" ? "Dokumentdetails" :
                    view === "template-form" ? `${selectedDocument?.title} ausfüllen` : ""
        }
        onBack={view !== "categories" ? handleBack : undefined}
        onAction={
          view === "upload" ? handleUploadSubmit :
            view === "share" ? handleShareSubmit :
              view === "template-form" ? handleTemplateSubmit : undefined
        }
        actionLabel={
          view === "upload" ? "Hochladen" :
            view === "share" ? "Teilen" :
              view === "template-form" ? "Speichern" : undefined
        }
        canAction={
          view === "upload" ? !!(uploadFile && uploadTitle.trim()) && !isUploading :
            view === "share" ? selectedServices.length > 0 && !isSharing :
              view === "template-form" ? !isSubmittingTemplate : undefined
        }
      />

      {/* Ladeindikatoren */}
      {(isUploading || isSharing || isSubmittingTemplate) && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner">⏳</div>
            <p>
              {isUploading ? "Dokument wird hochgeladen..." :
                isSharing ? "Dokument wird geteilt..." :
                  "Vorlage wird gespeichert..."}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
